import cloneDeep from 'lodash/cloneDeep';

import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import { generateFormObject } from '@/core/tools/forms/generate_form_object';
import { prepareFormObjectForSave } from '@/core/tools/forms/prepare_form_object_for_save';
import numeral from 'numeral';

interface ShiftAddWashFormState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    document: any;
    shift_plans: any[];
    form: any;
    full_form: any;
    schema: any;
    full_schema: any;
    storages: any[];
    targetForDelete: any[];
    rows: any[];
    initParams: any;
    typeLine: any;
    min_tank_value: any;

    meta: any;
}

const state: ShiftAddWashFormState = {
    loading: false,
    loaded: false,
    fatalError: false,

    document: null,
    shift_plans: [],
    storages: [],
    targetForDelete: [],
    rows: [],
    form: null,
    full_form: null,
    schema: null,
    full_schema: null,
    initParams: {},
    typeLine: null,
    min_tank_value: null,

    meta: {},
};

const initState: ShiftAddWashFormState = cloneDeep(state);

export const SHIFT_EQUIPMENT_DATA = 'GET_EQUIPMENT_DATA';
export const SHIFT_WASH_LINE_DATA = 'GET_SHIFT_WASH_LINE_DATA';
export const SHIFT_SETTING_VALUE_DATA = 'GET_SHIFT_SETTING_VALUE_DATA';

const line_mapper: any = {
    'vdp': 'shift_full_plan_vdp',
    'atom': 'shift_full_plan_atom',
    'custom': 'shift_full_plan_custom',
}

export const actions: ActionTree<ShiftAddWashFormState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    init({ commit, dispatch }, params) {
        const vm = (this as any)._vm;
        const queries: any[] = [];
        commit('SET_INIT_PARAMS', {initParams: params.initParams, typeLine: params.typeLine});
        if (!params.equipments || !params.equipments.length) {
            queries.push(
                vm.$q(SHIFT_EQUIPMENT_DATA)
                  .raw('documents.list')
                  .action({
                      columns: {
                          equipment_id: 'vv.id',
                      },
                      from: 's:voc_volumes',
                      from_as: 'vv',
                      where: {
                          $eq: ['vv.is_wash', {$: true}]
                      }
                  }),
            )
        }
        queries.push(
          vm.$q(SHIFT_WASH_LINE_DATA)
          .raw('documents.list')
          .action({
              columns: {
                  line_id: 'vv.id',
              },
              from: 's:voc_lines',
              from_as: 'vv',
              where: {
                  $eq: ['vv.templates', {$: 'wash'}]
              },
              limit: 1
          })
        )
        queries.push(
            vm.$q(SHIFT_SETTING_VALUE_DATA)
            .raw('documents.list')
            .action({
                columns: {
                    value: 's.value',
                },
                from: 's:settings',
                from_as: 's',
                where: {
                    $eq: ['s.name', {$: 'min_tank_value'}]
                },
                limit: 1
            })
        )
        return new Promise((resolve, reject) => {
            vm.$db(queries)
              .then((result: ServerResult) => {
                  const _res = queries.length > 2 ? result.getOperation(SHIFT_EQUIPMENT_DATA).getContent().documents : params.equipments
                  const _wash_line = result.getOperation(SHIFT_WASH_LINE_DATA).getContent().documents[0]
                  const _min_tank_value = result.getOperation(SHIFT_SETTING_VALUE_DATA).getContent().documents[0]
                  dispatch('loadData', {
                      data: _res,
                      line_id: _wash_line.line_id,
                      min_tank_value: _min_tank_value && _min_tank_value.value ? parseInt(_min_tank_value.value) : 50
                  });
              })
        })
    },

    loadData({ commit }, params) {
        console.log('params', params)
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(SHIFT_EQUIPMENT_DATA)
                    .raw('documents.list')
                    .action({
                        columns: {
                            // id: 'spve.id',
                            equipment_id: 'venw.equipment_id',
                            name: 'vv.name',
                            remains: 'vvr.remains',
                        },
                        from: 's:view_equipment_need_washing',
                        from_as: 'venw',
                        joins: [
                            {
                                join: 's:voc_volumes_washings',
                                as: 'vvw',
                                on: {
                                    $eq: ['venw.wash_id', 'vvw.id']
                                }
                            },
                            {
                                join: 's:washings',
                                as: 'w',
                                on: {
                                    $eq: ['vvw.washing_id', 'w.id']
                                }
                            },
                            {
                                join: 's:voc_volumes',
                                as: 'vv',
                                on: {
                                    $eq: ['vv.id', 'venw.equipment_id']
                                }
                            },
                            {
                                join: 's:view_volume_remains',
                                type: 'left',
                                as: 'vvr',
                                on: {
                                    $eq: ['vvr.volume_id', 'venw.equipment_id']
                                }
                            }
                        ],
                        where: {
                            $in: ['venw.equipment_id', {$: params.data.map((item: any) => item.equipment_id)}]
                        }
                    }),
            ]).then((result: ServerResult) => {
                const _res = result.getOperation(SHIFT_EQUIPMENT_DATA).getContent()
                commit('SET_EQUIPMENTS', {
                    documents: _res.documents,
                    line_id: params.line_id,
                    min_tank_value: params.min_tank_value,
                });

                commit('LOADED_SUCCESS');
                resolve(null);
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    saveForm({ commit }) {
        const vm = (this as any)._vm;
        console.log(state.rows)

        const rows = state.rows.filter((row: any) => row.washing_id).map((row: any, idx: number) => {
            return vm.$q('SHIFT_DATA_SAVE-' + idx)
                    .create('s:shift_plan_washing', {
                        equipment_id: row.equipment_id,
                        washing_id: row.washing_id,
                        shift_id: state.initParams.shift_id,
                        number: (state.initParams.numberStart) + idx + 'М',
                    })
        });

        return new Promise((resolve, reject) => {
            commit('START_LOADING');
            const schema = line_mapper[state.typeLine]

            vm.$db(rows).then((result: ServerResult) => {
                const _res = result.getOperationNames().map((name: string, idx: number) => {
                    const doc = result.getOperation(name).getContent().document;
                    return vm.$q('SHIFT_DATA_SAVE-' + idx)
                    .create(schema, {
                        wash_id: doc.id,
                        shift_id: state.initParams.shift_id,
                        number: doc.number,
                    })
                })
                vm.$db(_res).then((result: ServerResult) => {

                    commit('LOADED_SUCCESS');
                    resolve();
                })
                .catch((error: any) => {
                    console.error('errror', error)
                    reject(error);
                });
            })
            .catch((error: any) => {
                console.error('errror', error)
                reject(error);
            });
        })
    },

    setValue({commit}, {index, field, newValue}) {
        commit('CHANGE_VALUE', {index, field, newValue});
    }

};

export const mutations: MutationTree<ShiftAddWashFormState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = (initState as any)[key]
        }
    },

    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },

    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },

    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },
    SET_INIT_PARAMS: (state, {initParams, typeLine}) => {
        state.initParams = initParams;
        state.typeLine = typeLine;
    },

    SET_EQUIPMENTS(state, {documents, line_id, min_tank_value}) {
        state.min_tank_value = min_tank_value || 50
        state.rows = documents.map((item: any) => {
            item.washing_id = null
            item.line_id = line_id
            return item
        })
    },

    CHANGE_VALUE(state: ShiftAddWashFormState, {index, field, newValue}) {
        switch (field) {

            default:
                state.rows[index][field] = newValue;
        }
    },
};

const getters = {
    no_allow: (state: ShiftAddWashFormState, rootState: RootState) => {
       return state.rows.map((row: any) => {
           return row.remains >= state.min_tank_value && row.add_wash
       // @ts-ignore
       }).filter((row: any) => row).length != 0 && rootState.actor.roles.indexOf('admin') < 0;
    }
};

export const STORE_KEY = '$_shift_add_wash_form';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
